/**
 * Appwrite Preferences Master Template — SINGLE SOURCE OF TRUTH
 *
 * Defines the full key set for USER prefs, TEAM prefs, and user LABELS.
 * All writers (client services, admin drawers, backfill script) derive from
 * this template. Adding a key = add one line here; the merge-write helper
 * and backfill pick it up automatically.
 *
 * INVARIANTS enforced by this module:
 *   1. Appwrite updatePrefs() is a FULL REPLACEMENT — every writer must merge
 *      (fresh server read → template defaults → existing → updates).
 *   2. A key is either present with a real value or present with the STRING
 *      "null" — never absent, never an empty string, never a JSON null
 *      (JSON nulls freeze the Appwrite Console pref editor).
 *   3. Cross-system identifiers (client_id, lvl1_id, …) are never wiped by
 *      a partial save.
 *
 * Geo-derived values (country NAME, language, timezone, currency, state,
 * city) resolve through the Appwrite geo collections via geoService —
 * writers never hardcode them.
 */

/** Values a user/team `status` pref may hold. Gates access on non-payment etc. */
export const PREF_STATUS_VALUES = ['active', 'inactive', 'blocked', 'invited'] as const;
export type PrefStatus = (typeof PREF_STATUS_VALUES)[number];

/** Module flags live on TEAM prefs as string "true"/"false" (Appwrite convention). */
export type ModuleFlag = 'mod_leads' | 'mod_outreach' | 'mod_sales' | 'mod_workers' | 'mod_nexus' | 'mod_chat' | 'mod_goals';

/** A template entry: JSON type + the value written when the key is missing. */
interface PrefSpec<T> {
  type: 'string' | 'boolean' | 'number';
  default: T;
  /** Why this key exists — surfaced in the backfill dry-run report. */
  note?: string;
}

type PrefSpecs = Record<string, PrefSpec<string | boolean | number | null>>;

// ── USER PREFS ───────────────────────────────────────────────────────────────

const USER_PREF_SPECS: PrefSpecs = {
  // Identity — written at registration, editable in Settings → Account
  full_name:                { type: 'string', default: null },
  job_title:                { type: 'string', default: null },
  country:                  { type: 'string', default: null, note: 'FULL country name from geo_countries (e.g. "Spain"), NOT the ISO2 code' },
  country_code:             { type: 'string', default: null, note: 'ISO2 code from geo_countries (e.g. "ES")' },
  state:                    { type: 'string', default: null, note: 'State/province name from geo_states' },
  city:                     { type: 'string', default: null, note: 'City name from geo_cities' },
  registration_completed_at:{ type: 'string', default: null },

  // Cross-system identifiers — VITAL, never wipe
  lvl1_id:                  { type: 'string', default: null },
  clickup_id:               { type: 'string', default: null, note: 'Internal CRM id assigned by Brainiacs' },
  nexus_url:                { type: 'string', default: null },
  profilePictureUrl:        { type: 'string', default: null, note: 'camelCase kept — oauth-profile-sync + N8N read it' },

  // Access control
  status:                   { type: 'string', default: 'active', note: `Enum: ${PREF_STATUS_VALUES.join(' | ')}` },

  // App preferences
  language:                 { type: 'string', default: 'en-GB', note: 'BCP 47 tag; primary from geo_countries.language_codes' },
  timezone:                 { type: 'string', default: null, note: 'IANA timezone, e.g. Europe/Madrid — resolved from geo_states' },
  currency:                 { type: 'string', default: null, note: 'ISO currency code, e.g. EUR — resolved from geo_countries' },
  theme:                    { type: 'string', default: 'dark' },
  onboarding_completed:     { type: 'boolean', default: false },
  background_effect:        { type: 'string', default: 'none', note: 'Minimalist is the default; aurora/lightrays/vanta-waves optional' },
  background_opacity:       { type: 'number', default: 0.7 },
  background_aurora_colors: { type: 'string', default: null, note: 'Comma-separated 3 hex stops for the Aurora ramp, e.g. #EC4899,#4815B4,#7C3AED' },
};

// ── TEAM PREFS ───────────────────────────────────────────────────────────────

const TEAM_PREF_SPECS: PrefSpecs = {
  // Localization — geo-resolved
  language:                 { type: 'string', default: 'en-GB', note: 'BCP 47 tag; primary from geo_countries.language_codes' },
  timezone:                 { type: 'string', default: 'Europe/Oslo' },
  currency:                 { type: 'string', default: 'EUR' },
  country:                  { type: 'string', default: null, note: 'FULL country name from geo_countries' },
  country_code:             { type: 'string', default: null },
  state:                    { type: 'string', default: null },
  company_website:          { type: 'string', default: null },

  // Billing / access control
  billing_email:            { type: 'string', default: null },
  status:                   { type: 'string', default: 'active', note: `Enum: ${PREF_STATUS_VALUES.join(' | ')}` },
  partner:                  { type: 'string', default: 'false', note: 'String "true"/"false" — team-pref convention' },

  // External-account identifiers — canonical PLURAL key names
  lvl1_id:                  { type: 'string', default: null },
  stripe_id:                { type: 'string', default: null },
  clickup_id:               { type: 'string', default: null, note: 'Internal CRM id assigned by Brainiacs' },
  seller_ids:               { type: 'string', default: null },
  instantly_org:            { type: 'string', default: '5c0c2c07-9ffd-40bc-91ca-2ac366da62fc' },
  Instantly_domains:        { type: 'string', default: null },
  unipile_whatsapp_ids:     { type: 'string', default: null },
  unipile_linkedin_ids:     { type: 'string', default: null },
  twilio_account_id:        { type: 'string', default: null },
  twilio_phones_ids:        { type: 'string', default: null },
  elevenlabs_phone_ids:     { type: 'string', default: null },

  // Module activation flags — string "true"/"false"
  mod_leads:                { type: 'string', default: 'true' },
  mod_outreach:             { type: 'string', default: 'false' },
  mod_sales:                { type: 'string', default: 'false' },
  mod_workers:              { type: 'string', default: 'false' },
  mod_nexus:                { type: 'string', default: 'false' },
  mod_chat:                 { type: 'string', default: 'false' },
  mod_goals:                { type: 'string', default: 'false', note: 'Goals Intelligence — next-gen OKRs (MODULE_CATALOG field "goals")' },

  // Branding
  brand_primary_colour:     { type: 'string', default: null, note: '#RRGGBB' },
  logo_url:                 { type: 'string', default: null, note: 'Canonical — the team\'s uploaded logo URL' },
};

// ── LABELS ───────────────────────────────────────────────────────────────────

/** User labels the platform itself assigns/gates on. Appwrite: 1-36 chars. */
export const USER_LABELS_TEMPLATE = {
  superadmin: 'Full platform admin — gates admin-prefs-manager writes',
  teamadmin: 'May edit their own team prefs',
  nexus: 'Nexus personal AI server customers',
} as const;

// ── DERIVED EXPORTS ─────────────────────────────────────────────────────────

export const USER_PREFS_TEMPLATE_KEYS = Object.keys(USER_PREF_SPECS) as string[];
export const TEAM_PREFS_TEMPLATE_KEYS = Object.keys(TEAM_PREF_SPECS) as string[];

const freeze = <T>(o: T): Readonly<T> => Object.freeze(o);

/** user / team discriminator for the helpers below. */
export type PrefStore = 'user' | 'team';

const SPECS: Record<PrefStore, PrefSpecs> = {
  user: USER_PREF_SPECS,
  team: TEAM_PREF_SPECS,
};

/**
 * Build the full default prefs object for a store — every template key with
 * its default value. The base for backfill and for seeding new teams.
 */
export function defaultPrefs(store: PrefStore): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, spec] of Object.entries(SPECS[store])) out[key] = spec.default;
  return out;
}

/**
 * Normalize one existing prefs object against the template (STORAGE form in,
 * STORAGE form out — "null" strings stay "null"):
 *   - missing keys → filled with the template default (never absent)
 *   - `''` / undefined / JSON null → the STRING "null" (storage form)
 *   - booleans stored as strings ('true'/'false' module convention) keep their
 *     string form; `onboarding_completed` is coerced to a real boolean
 *   - keys OUTSIDE the template are preserved as-is (N8N/external writers
 *     may own them)
 *
 * Returns changed=false when nothing changed, so callers can skip the write.
 */
export function normalizePrefs(
  store: PrefStore,
  existing: Record<string, any> | null | undefined,
): { prefs: Record<string, any>; changed: boolean } {
  const source = existing || {};
  const out: Record<string, any> = { ...source };
  let changed = false;

  // 1. JSON null / undefined / empty-string → storage "null".
  for (const [key, value] of Object.entries(out)) {
    if (value === undefined || value === '' || value === null) {
      out[key] = STORED_NULL;
      changed = true;
    }
  }

  // 2. Fill missing template keys with defaults (encoded to storage form).
  for (const key of Object.keys(SPECS[store])) {
    if (!(key in out)) {
      out[key] = SPECS[store][key].default === null ? STORED_NULL : SPECS[store][key].default;
      changed = true;
    }
  }

  // 3. Type coercions (only for keys whose stored type disagrees with spec).
  for (const [key, spec] of Object.entries(SPECS[store])) {
    if (!(key in source)) continue; // just defaulted above
    const value = out[key];
    if (value === STORED_NULL) continue;
    if (spec.type === 'boolean' && typeof value === 'string') {
      out[key] = value.toLowerCase() === 'true';
      changed = true;
    } else if (spec.type === 'number' && typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      out[key] = Number(value);
      changed = true;
    }
  }

  return { prefs: out, changed };
}

/**
 * STORAGE FORM: Appwrite stores empty pref values as the STRING "null".
 *
 * Real JSON nulls freeze the Appwrite Console pref editor (can't save), and
 * the N8N writers already store "null" strings — so "null" is the convention.
 *
 * Boundary rule: every WRITE encodes (null → "null"), every READ decodes
 * ("null" → null). Internal code only ever sees real nulls, so truthiness
 * fallbacks (`prefs.language || 'en-GB'`) keep working.
 */
export const STORED_NULL = 'null';

export function encodeStoragePrefs(prefs: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(prefs)) out[key] = value === null ? STORED_NULL : value;
  return out;
}

export function decodeStoragePrefs(prefs: Record<string, any> | null | undefined): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(prefs || {})) out[key] = value === STORED_NULL ? null : value;
  return out;
}

/** Decode a single pref value read straight from a raw Appwrite user object. */
export const decodePrefValue = (value: unknown): any =>
  value === STORED_NULL ? null : value;

/**
 * Merge updates into an existing prefs object for writing. `updates` win,
 * unknown keys pass through (open allowlist — external systems own some keys).
 * Result is encoded to storage form ("null" strings) — ready for updatePrefs.
 */
export function mergePrefs(
  store: PrefStore,
  existing: Record<string, any> | null | undefined,
  updates: Record<string, any>,
): Record<string, any> {
  return encodeStoragePrefs({ ...defaultPrefs(store), ...(existing || {}), ...updates });
}

/**
 * Sync one store's prefs against the template — ADD missing keys, NEVER remove
 * anything (even unknown keys pass through). Returns a diff report.
 *
 *   - `prev`: the current prefs (storage form, as read from Appwrite).
 *   - `next`: the prefs to write (storage form, with missing keys filled).
 *   - `addedKeys`: template keys that were missing and are now filled.
 *   - `unknownKeys`: keys in `prev` that are NOT in the template (highlighted
 *     in the admin UI so the operator knows they exist).
 *   - `changed`: true if next differs from prev.
 */
/**
 * Geo-normalized validators for common pref keys. Each returns either the
 * canonical form or the string "null" (storage form) on mismatch.
 *
 * Import geoService on-demand (lazy) so this module doesn't force a dep
 * at module scope — the sync script calls these explicitly.
 */

export type GeoValidators = {
  countryName: (iso2: string) => Promise<string | null>;
  countryIso2: (nameOrCode: string) => Promise<string | null>;
  primaryLocale: (iso2: string) => Promise<string | null>;
  timezoneForState: (countryIso2: string, stateName: string) => Promise<string | null>;
  primaryTimezoneForCountry: (iso2: string) => Promise<string | null>;
  currencyForCountry: (iso2: string) => Promise<string | null>;
};

/**
 * Validate and normalize user prefs against geo data + locale constants.
 *
 * Rules:
 *   language   → normalizeLocale() against the supported set; if the stored
 *                value doesn't resolve, try primaryLocale(country_code)
 *   country    → if the value looks like an ISO2 code (2 alpha), replace with
 *                the full name from geo_countries
 *   country_code → if the value looks like a country NAME, replace with the
 *                ISO2 code
 *   timezone   → if the value doesn't match any known zone, fall back to
 *                primaryTimezoneForCountry(country_code)
 *   currency   → if the value doesn't match the country's currency, overwrite
 *   state, city → no validation (free-form text)
 *
 * Returns { next, changes } — changes is a list of human-readable diffs.
 * Applies ONLY to user prefs (team prefs handled similarly).
 */
export async function validatePrefs(
  store: PrefStore,
  prefs: Record<string, any>,
  geo: GeoValidators,
  normalizeLocale: (locale: string) => string,
): Promise<{ next: Record<string, any>; changes: string[] }> {
  const next = { ...prefs };
  const changes: string[] = [];
  const countryCode = next.country_code as string | undefined;

  // ── country: if it looks like an ISO2 code, replace with full name ──────
  if (typeof next.country === 'string' && /^[A-Za-z]{2}$/.test(next.country.trim())) {
    const name = await geo.countryName(next.country.trim().toUpperCase());
    if (name && name !== next.country) {
      changes.push(`country "${next.country}" → "${name}" (code→name)`);
      next.country = name;
    }
  }

  // ── country_code: if it looks like a name, replace with ISO2 ────────────
  if (typeof next.country_code === 'string' && /^[A-Za-z\s]{4,}$/.test(next.country_code.trim())) {
    const iso2 = await geo.countryIso2(next.country_code.trim());
    if (iso2) {
      changes.push(`country_code "${next.country_code}" → "${iso2}" (name→code)`);
      next.country_code = iso2;
    }
  }

  // ── language: normalize via locale constants ────────────────────────────
  if (typeof next.language === 'string' && next.language.trim() !== '' && next.language !== 'null') {
    const normalized = normalizeLocale(next.language);
    if (normalized !== next.language) {
      changes.push(`language "${next.language}" → "${normalized}"`);
      next.language = normalized;
    }
  }

  // ── timezone: validate against geo data ─────────────────────────────────
  if (typeof next.timezone === 'string' && next.timezone.trim() !== '' && next.timezone !== 'null') {
    // Known IANA zones start with a region/ prefix; anything else is suspect.
    if (!next.timezone.includes('/')) {
      // Try to resolve from country+state or country
      if (countryCode) {
        const state = next.state as string | undefined;
        const resolved = state
          ? await geo.timezoneForState(countryCode, state)
          : await geo.primaryTimezoneForCountry(countryCode);
        if (resolved) {
          changes.push(`timezone "${next.timezone}" → "${resolved}" (invalid format)`);
          next.timezone = resolved;
        } else {
          changes.push(`timezone "${next.timezone}" → null (unresolvable)`);
          next.timezone = 'null';
        }
      } else {
        changes.push(`timezone "${next.timezone}" → null (unresolvable)`);
        next.timezone = 'null';
      }
    }
  }

  // ── currency: validate against country ──────────────────────────────────
  if (typeof next.currency === 'string' && next.currency.trim() !== '' && next.currency !== 'null' && countryCode) {
    const expected = await geo.currencyForCountry(countryCode);
    if (expected && next.currency !== expected) {
      changes.push(`currency "${next.currency}" → "${expected}"`);
      next.currency = expected;
    }
  }

  return { next, changes };
}

export function syncPrefs(
  store: PrefStore,
  existing: Record<string, any> | null | undefined,
): { prev: Record<string, any>; next: Record<string, any>; addedKeys: string[]; unknownKeys: string[]; changed: boolean } {
  const prev = existing || {};
  const templateKeys = new Set(Object.keys(SPECS[store]));
  const addedKeys: string[] = [];
  const unknownKeys: string[] = [];

  // Start with template defaults (encoded) then overlay existing — this fills
  // missing keys while keeping every existing value AND every unknown key.
  const next: Record<string, any> = { ...encodeStoragePrefs(defaultPrefs(store)) };

  for (const [key, value] of Object.entries(prev)) {
    // Normalize JSON null and empty-string → storage "null".
    next[key] = (value === null || value === undefined || value === '') ? STORED_NULL : value;
    if (!templateKeys.has(key)) unknownKeys.push(key);
  }

  // Track which template keys were added (not in prev).
  for (const key of Object.keys(SPECS[store])) {
    if (!(key in prev)) addedKeys.push(key);
  }

  // Also detect when existing JSON nulls were converted to storage "null".
  const hasStorageConversion = Object.entries(prev).some(([k, v]) => v === null && next[k] === STORED_NULL);
  const changed = addedKeys.length > 0 || hasStorageConversion;
  return { prev, next, addedKeys, unknownKeys, changed };
}
